import { useState, useEffect } from "react";
import { useChecksCatalog } from "../../hooks/useChecks.js";
import styles from "./ProfileChecks.module.css";

interface ProfileChecksProps {
  config: Record<string, unknown>;
  editMode?: boolean;
  onSave?: (newConfig: Record<string, unknown>) => void;
  isSaving?: boolean;
}

function isEnabled(value: unknown): boolean {
  if (value === true) return true;
  if (value && typeof value === "object" && "enabled" in value) {
    return (value as { enabled: boolean }).enabled !== false;
  }
  return false;
}

function getParams(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && "enabled" in value) {
    const { enabled: _enabled, ...rest } = value as Record<string, unknown>;
    return Object.keys(rest).length > 0 ? rest : null;
  }
  return null;
}

export function ProfileChecks({ config, editMode = false, onSave, isSaving = false }: ProfileChecksProps) {
  const { data: catalog = [], isLoading } = useChecksCatalog();
  const [localConfig, setLocalConfig] = useState<Record<string, unknown>>(config);

  useEffect(() => {
    setLocalConfig(config);
  }, [config]);

  if (isLoading) return <p className={styles.loading}>Loading checks…</p>;

  function toggleCheck(slug: string) {
    const original = config[slug];
    const current = localConfig[slug];
    const enabled = current !== undefined ? isEnabled(current) : false;
    let next: unknown;
    if (enabled) {
      // disable it
      if (current && typeof current === "object" && "enabled" in current) {
        next = { ...(current as Record<string, unknown>), enabled: false };
      } else {
        next = false;
      }
    } else {
      // re-enable: restore original if it was an object, else set true
      if (original && typeof original === "object" && "enabled" in (original as Record<string, unknown>)) {
        next = { ...(original as Record<string, unknown>), enabled: true };
      } else {
        next = true;
      }
    }
    setLocalConfig((prev) => ({ ...prev, [slug]: next }));
  }

  // Group catalog by category
  const byCategory = new Map<string, typeof catalog>();
  for (const check of catalog) {
    const list = byCategory.get(check.category) ?? [];
    list.push(check);
    byCategory.set(check.category, list);
  }

  // Collect unknown keys from config not in catalog (legacy/custom)
  const catalogSlugs = new Set(catalog.map((c) => c.slug));
  const unknownKeys = Object.keys(localConfig).filter((k) => !catalogSlugs.has(k));

  return (
    <div className={styles.root}>
      {editMode && (
        <div className={styles.editBar}>
          <button
            className={styles.resetBtn}
            onClick={() => setLocalConfig(config)}
            disabled={isSaving}
          >
            Reset
          </button>
          <button
            className={styles.saveBtn}
            onClick={() => onSave?.(localConfig)}
            disabled={isSaving}
          >
            {isSaving ? "Saving…" : "Save changes"}
          </button>
        </div>
      )}
      {[...byCategory.entries()].map(([category, checks]) => (
        <section key={category} className={styles.category}>
          <h4 className={styles.categoryTitle}>{category}</h4>
          <div className={styles.grid}>
            {checks.map((c) => {
              const val = localConfig[c.slug];
              // If not in config at all → treat as disabled
              const enabled = val !== undefined ? isEnabled(val) : false;
              const params = getParams(val);
              return (
                <div
                  key={c.slug}
                  className={`${styles.check} ${enabled ? styles.enabled : styles.disabled} ${editMode ? styles.clickable : ""}`}
                  onClick={editMode ? () => toggleCheck(c.slug) : undefined}
                  role={editMode ? "button" : undefined}
                  tabIndex={editMode ? 0 : undefined}
                  onKeyDown={editMode ? (e) => { if (e.key === "Enter" || e.key === " ") toggleCheck(c.slug); } : undefined}
                >
                  <div className={styles.checkHeader}>
                    <span className={styles.checkIcon}>{enabled ? "✓" : "✗"}</span>
                    <span className={styles.checkName}>{c.name}</span>
                    {c.description && (
                      <span
                        className={styles.infoIcon}
                        data-tooltip={c.description}
                        onClick={(e) => e.stopPropagation()}
                      >ℹ</span>
                    )}
                  </div>
                  {params && (
                    <div className={styles.params}>
                      {Object.entries(params).map(([k, v]) => (
                        <span key={k} className={styles.param}>
                          <span className={styles.paramKey}>{k}</span>
                          <span className={styles.paramVal}>{String(v)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
      {unknownKeys.length > 0 && (
        <section className={styles.category}>
          <h4 className={styles.categoryTitle}>Other</h4>
          <div className={styles.grid}>
            {unknownKeys.map((k) => (
              <div
                key={k}
                className={`${styles.check} ${isEnabled(localConfig[k]) ? styles.enabled : styles.disabled} ${editMode ? styles.clickable : ""}`}
                onClick={editMode ? () => toggleCheck(k) : undefined}
                role={editMode ? "button" : undefined}
                tabIndex={editMode ? 0 : undefined}
                onKeyDown={editMode ? (e) => { if (e.key === "Enter" || e.key === " ") toggleCheck(k); } : undefined}
              >
                <div className={styles.checkHeader}>
                  <span className={styles.checkIcon}>{isEnabled(localConfig[k]) ? "✓" : "✗"}</span>
                  <code className={styles.checkSlug}>{k}</code>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
